import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Upload, AlertCircle, CheckCircle2, Users, ArrowRight, X, Download, Trash2, Wrench, Calendar } from 'lucide-react';
import { collection, doc, setDoc, getDocs, query, deleteDoc, updateDoc, deleteField, getDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface OrgRecord {
  name: string;
  title: string;
  email: string;
  managerEmail: string;
}

interface ImportPreview {
  toCreate: OrgRecord[];
  toUpdate: { record: OrgRecord; changes: string[] }[];
  unchanged: OrgRecord[];
  errors: { row: number; message: string }[];
}

interface ExistingAssignment {
  employeeEmail: string;
  employeeName: string;
  managerEmail: string;
  employeeTitle: string;
}

const OrgChartImport = () => {
  const { isDeveloper, user } = useAuth();
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importStats, setImportStats] = useState<{ created: number; updated: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState<number | null>(null);
  
  // Review period settings
  const [reviewQuarter, setReviewQuarter] = useState<number>(Math.ceil((new Date().getMonth() + 1) / 3));
  const [reviewYear, setReviewYear] = useState<number>(new Date().getFullYear());
  const [selfReviewDate, setSelfReviewDate] = useState<string>('');
  const [managerReviewDate, setManagerReviewDate] = useState<string>('');
  const [currentSettings, setCurrentSettings] = useState<{
    selfReviewOpenDate: Date | null;
    managerReviewOpenDate: Date | null;
    quarter: number | null;
    year: number | null;
  }>({ selfReviewOpenDate: null, managerReviewOpenDate: null, quarter: null, year: null });
  const [savingReviewSettings, setSavingReviewSettings] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const canAccess = isDeveloper || isSuperAdmin;

  // Fetch current review period settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setCurrentSettings({
            selfReviewOpenDate: data.selfReviewOpenDate?.toDate() || null,
            managerReviewOpenDate: data.managerReviewOpenDate?.toDate() || null,
            quarter: data.selfReviewQuarter || null,
            year: data.selfReviewYear || null,
          });
          
          if (data.selfReviewOpenDate) {
            setSelfReviewDate(data.selfReviewOpenDate.toDate().toISOString().split('T')[0]);
          }
          if (data.managerReviewOpenDate) {
            setManagerReviewDate(data.managerReviewOpenDate.toDate().toISOString().split('T')[0]);
          }
          if (data.selfReviewQuarter) {
            setReviewQuarter(data.selfReviewQuarter);
          }
          if (data.selfReviewYear) {
            setReviewYear(data.selfReviewYear);
          }
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    
    if (canAccess) {
      fetchSettings();
    }
  }, [canAccess]);

  const handleSaveReviewSettings = async () => {
    setSavingReviewSettings(true);
    try {
      const settingsRef = doc(db, 'settings', 'global');
      await setDoc(settingsRef, {
        selfReviewOpenDate: selfReviewDate ? Timestamp.fromDate(new Date(selfReviewDate)) : null,
        managerReviewOpenDate: managerReviewDate ? Timestamp.fromDate(new Date(managerReviewDate)) : null,
        selfReviewQuarter: reviewQuarter,
        selfReviewYear: reviewYear,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      
      setCurrentSettings({
        selfReviewOpenDate: selfReviewDate ? new Date(selfReviewDate) : null,
        managerReviewOpenDate: managerReviewDate ? new Date(managerReviewDate) : null,
        quarter: reviewQuarter,
        year: reviewYear,
      });
      
      alert('Review period settings saved!');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save review settings');
    } finally {
      setSavingReviewSettings(false);
    }
  };

  const handleClearReviewSettings = async () => {
    setSavingReviewSettings(true);
    try {
      const settingsRef = doc(db, 'settings', 'global');
      await setDoc(settingsRef, {
        selfReviewOpenDate: null,
        managerReviewOpenDate: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      
      setSelfReviewDate('');
      setManagerReviewDate('');
      setCurrentSettings({
        selfReviewOpenDate: null,
        managerReviewOpenDate: null,
        quarter: reviewQuarter,
        year: reviewYear,
      });
      
      alert('Review period settings cleared!');
    } catch (err) {
      console.error('Error clearing settings:', err);
      setError('Failed to clear review settings');
    } finally {
      setSavingReviewSettings(false);
    }
  };

  const findDuplicates = async () => {
    const assignmentsRef = collection(db, 'team_assignments');
    const snapshot = await getDocs(query(assignmentsRef));
    
    // Group by email
    const byEmail = new Map<string, { id: string; isEmailId: boolean }[]>();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const email = data.employeeEmail?.toLowerCase();
      if (!email) return;
      
      const isEmailId = doc.id.toLowerCase() === email;
      
      if (!byEmail.has(email)) {
        byEmail.set(email, []);
      }
      byEmail.get(email)!.push({ id: doc.id, isEmailId });
    });
    
    // Find duplicates (emails with more than one record)
    const duplicates: string[] = [];
    byEmail.forEach((records) => {
      if (records.length > 1) {
        // Keep the one with email as ID, delete others
        records.forEach(r => {
          if (!r.isEmailId) {
            duplicates.push(r.id);
          }
        });
      }
    });
    
    return duplicates;
  };

  const handleCheckDuplicates = async () => {
    const dups = await findDuplicates();
    setDuplicateCount(dups.length);
  };

  const handleCleanupDuplicates = async () => {
    setCleaningUp(true);
    try {
      const duplicates = await findDuplicates();
      
      for (const docId of duplicates) {
        await deleteDoc(doc(db, 'team_assignments', docId));
      }
      
      setDuplicateCount(0);
      alert(`Cleaned up ${duplicates.length} duplicate records`);
    } catch (err) {
      setError('Failed to clean up duplicates');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleFixTitleField = async () => {
    setCleaningUp(true);
    try {
      const assignmentsRef = collection(db, 'team_assignments');
      const snapshot = await getDocs(query(assignmentsRef));
      
      let fixed = 0;
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        // If it has 'title' but not 'employeeTitle', fix it
        if (data.title !== undefined && data.employeeTitle === undefined) {
          await updateDoc(doc(db, 'team_assignments', docSnap.id), {
            employeeTitle: data.title || '',
            title: deleteField(),
          });
          fixed++;
        }
      }
      
      alert(`Fixed ${fixed} records (renamed title → employeeTitle)`);
    } catch (err) {
      console.error('Fix title error:', err);
      setError('Failed to fix title fields: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setCleaningUp(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const assignmentsRef = collection(db, 'team_assignments');
      const snapshot = await getDocs(query(assignmentsRef));
      
      const rows = [['Name', 'Title', 'Email', 'Manager Email']];
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        rows.push([
          data.employeeName || '',
          data.employeeTitle || '',
          data.employeeEmail || '',
          data.managerEmail || '',
        ]);
      });
      
      const csv = rows.map(row => 
        row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team_assignments_backup_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const parseCSV = (text: string): OrgRecord[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIdx = headers.findIndex(h => h === 'name');
    const titleIdx = headers.findIndex(h => h === 'title');
    const emailIdx = headers.findIndex(h => h === 'email');
    const managerIdx = headers.findIndex(h => h.includes('manager'));

    if (nameIdx === -1 || emailIdx === -1) {
      throw new Error('CSV must have Name and Email columns');
    }

    const records: OrgRecord[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Handle quoted values with commas
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const email = values[emailIdx]?.toLowerCase().trim();
      if (!email) continue;

      records.push({
        name: values[nameIdx]?.trim() || '',
        title: titleIdx !== -1 ? values[titleIdx]?.trim() || '' : '',
        email,
        managerEmail: managerIdx !== -1 ? values[managerIdx]?.toLowerCase().trim() || '' : '',
      });
    }

    return records;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setPreview(null);
    setImportComplete(false);
    setImportStats(null);

    try {
      const text = await file.text();
      const records = parseCSV(text);
      
      if (records.length === 0) {
        setError('No valid records found in CSV');
        return;
      }

      await generatePreview(records);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV');
    }
  };

  const generatePreview = async (records: OrgRecord[]) => {
    // Fetch existing team_assignments
    const assignmentsRef = collection(db, 'team_assignments');
    const snapshot = await getDocs(query(assignmentsRef));
    
    const existing = new Map<string, ExistingAssignment>();
    snapshot.docs.forEach(doc => {
      const data = doc.data() as ExistingAssignment;
      existing.set(data.employeeEmail?.toLowerCase(), data);
    });

    const preview: ImportPreview = {
      toCreate: [],
      toUpdate: [],
      unchanged: [],
      errors: [],
    };

    records.forEach((record, idx) => {
      // Validate
      if (!record.email) {
        preview.errors.push({ row: idx + 2, message: 'Missing email' });
        return;
      }
      if (!record.name) {
        preview.errors.push({ row: idx + 2, message: `Missing name for ${record.email}` });
        return;
      }

      const existingRecord = existing.get(record.email.toLowerCase());

      if (!existingRecord) {
        preview.toCreate.push(record);
      } else {
        const changes: string[] = [];
        
        if (existingRecord.employeeName !== record.name) {
          changes.push(`Name: "${existingRecord.employeeName}" → "${record.name}"`);
        }
        if (existingRecord.employeeTitle !== record.title) {
          changes.push(`Title: "${existingRecord.employeeTitle || '(none)'}" → "${record.title || '(none)'}"`);
        }
        if (existingRecord.managerEmail?.toLowerCase() !== record.managerEmail?.toLowerCase()) {
          changes.push(`Manager: "${existingRecord.managerEmail || '(none)'}" → "${record.managerEmail || '(none)'}"`);
        }

        if (changes.length > 0) {
          preview.toUpdate.push({ record, changes });
        } else {
          preview.unchanged.push(record);
        }
      }
    });

    setPreview(preview);
  };

  const handleImport = async () => {
    if (!preview) return;

    setImporting(true);
    setError(null);

    try {
      let created = 0;
      let updated = 0;

      // Create new assignments
      for (const record of preview.toCreate) {
        const docRef = doc(db, 'team_assignments', record.email.toLowerCase());
        await setDoc(docRef, {
          employeeEmail: record.email.toLowerCase(),
          employeeName: record.name,
          managerEmail: record.managerEmail?.toLowerCase() || null,
          employeeTitle: record.title || '',
        });
        created++;
      }

      // Update existing assignments
      for (const { record } of preview.toUpdate) {
        const docRef = doc(db, 'team_assignments', record.email.toLowerCase());
        await setDoc(docRef, {
          employeeEmail: record.email.toLowerCase(),
          employeeName: record.name,
          managerEmail: record.managerEmail?.toLowerCase() || null,
          employeeTitle: record.title || '',
        }, { merge: true });
        updated++;
      }

      setImportStats({ created, updated });
      setImportComplete(true);
      setPreview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    setPreview(null);
    setError(null);
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only developers and super admins can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-8 h-8 text-cps-primary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
              <p className="text-gray-600">Manage org chart and review periods</p>
            </div>
          </div>

          {/* Review Period Settings */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-purple-800 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Review Period Settings
            </h3>
            
            {currentSettings.selfReviewOpenDate && (
              <div className="mb-4 p-3 bg-white rounded-lg border border-purple-200">
                <p className="text-sm text-purple-700">
                  <strong>Current:</strong> Q{currentSettings.quarter} {currentSettings.year}
                </p>
                <p className="text-sm text-purple-700">
                  Self-review: {currentSettings.selfReviewOpenDate <= new Date() 
                    ? <span className="text-green-600 font-medium">Open now</span> 
                    : `Opens ${currentSettings.selfReviewOpenDate.toLocaleDateString()}`}
                </p>
                {currentSettings.managerReviewOpenDate && (
                  <p className="text-sm text-purple-700">
                    Manager review: {currentSettings.managerReviewOpenDate <= new Date() 
                      ? <span className="text-green-600 font-medium">Open now</span> 
                      : `Opens ${currentSettings.managerReviewOpenDate.toLocaleDateString()}`}
                  </p>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Quarter</label>
                <select
                  value={reviewQuarter}
                  onChange={(e) => setReviewQuarter(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value={1}>Q1</option>
                  <option value={2}>Q2</option>
                  <option value={3}>Q3</option>
                  <option value={4}>Q4</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Year</label>
                <select
                  value={reviewYear}
                  onChange={(e) => setReviewYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  {[reviewYear - 1, reviewYear, reviewYear + 1].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Self-Review Opens</label>
                <input
                  type="date"
                  value={selfReviewDate}
                  onChange={(e) => setSelfReviewDate(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Manager Review Opens</label>
                <input
                  type="date"
                  value={managerReviewDate}
                  onChange={(e) => setManagerReviewDate(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleSaveReviewSettings}
                disabled={savingReviewSettings || !selfReviewDate}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {savingReviewSettings ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4" />
                )}
                Save Review Period
              </button>
              {currentSettings.selfReviewOpenDate && (
                <button
                  onClick={handleClearReviewSettings}
                  disabled={savingReviewSettings}
                  className="px-4 py-2 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <hr className="my-6 border-gray-200" />
          
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Org Chart Import</h2>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-blue-800 mb-2">CSV Format</h3>
                <p className="text-sm text-blue-700 mb-2">
                  Your CSV should have these headers: <code className="bg-blue-100 px-1 rounded">Name, Title, Email, Manager Email</code>
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> This only updates team assignments. Goals are never modified.
                </p>
              </div>
              <button
                onClick={handleDownloadBackup}
                disabled={downloadingBackup}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {downloadingBackup ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Backup Current
              </button>
            </div>
          </div>

          {/* Cleanup duplicates section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-yellow-800 mb-2">Cleanup Duplicates</h3>
                <p className="text-sm text-yellow-700">
                  Remove old records with random IDs (keeps email-based IDs).
                  {duplicateCount !== null && (
                    <strong className="ml-1">
                      {duplicateCount === 0 ? 'No duplicates found!' : `Found ${duplicateCount} duplicates.`}
                    </strong>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCheckDuplicates}
                  className="px-3 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Check
                </button>
                {duplicateCount !== null && duplicateCount > 0 && (
                  <button
                    onClick={handleCleanupDuplicates}
                    disabled={cleaningUp}
                    className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {cleaningUp ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delete {duplicateCount}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Fix title field section */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-orange-800 mb-2">Fix Title Field</h3>
                <p className="text-sm text-orange-700">
                  Rename <code className="bg-orange-100 px-1 rounded">title</code> → <code className="bg-orange-100 px-1 rounded">employeeTitle</code> for records that have the wrong field name.
                </p>
              </div>
              <button
                onClick={handleFixTitleField}
                disabled={cleaningUp}
                className="px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {cleaningUp ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Wrench className="w-4 h-4" />
                )}
                Fix Fields
              </button>
            </div>
          </div>

          {/* Success message */}
          {importComplete && importStats && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-800">Import Complete</h3>
                <p className="text-sm text-green-700">
                  Created {importStats.created} new records, updated {importStats.updated} existing records.
                </p>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* File upload */}
          {!preview && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-cps-primary transition-colors">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <label className="cursor-pointer">
                <span className="text-cps-primary font-medium hover:underline">Choose a CSV file</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              <p className="text-sm text-gray-500 mt-2">or drag and drop</p>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{preview.toCreate.length}</p>
                  <p className="text-sm text-green-600">To Create</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-700">{preview.toUpdate.length}</p>
                  <p className="text-sm text-yellow-600">To Update</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-700">{preview.unchanged.length}</p>
                  <p className="text-sm text-gray-600">Unchanged</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">{preview.errors.length}</p>
                  <p className="text-sm text-red-600">Errors</p>
                </div>
              </div>

              {/* New records */}
              {preview.toCreate.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    New Records ({preview.toCreate.length})
                  </h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Title</th>
                          <th className="text-left p-2">Manager</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.toCreate.map((r, i) => (
                          <tr key={i} className="border-t border-gray-200">
                            <td className="p-2">{r.name}</td>
                            <td className="p-2 text-gray-600">{r.email}</td>
                            <td className="p-2 text-gray-600">{r.title || '-'}</td>
                            <td className="p-2 text-gray-600">{r.managerEmail || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Updates */}
              {preview.toUpdate.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    Updates ({preview.toUpdate.length})
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {preview.toUpdate.map(({ record, changes }, i) => (
                      <div key={i} className="bg-yellow-50 rounded-lg p-3">
                        <p className="font-medium text-gray-900">{record.name}</p>
                        <p className="text-xs text-gray-500 mb-1">{record.email}</p>
                        {changes.map((change, j) => (
                          <p key={j} className="text-sm text-yellow-700 flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" />
                            {change}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {preview.errors.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Errors ({preview.errors.length})
                  </h3>
                  <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {preview.errors.map((err, i) => (
                      <p key={i} className="text-sm text-red-700">
                        Row {err.row}: {err.message}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || (preview.toCreate.length === 0 && preview.toUpdate.length === 0)}
                  className="flex-1 px-4 py-2 bg-cps-primary text-white rounded-lg hover:bg-cps-light transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {importing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Import {preview.toCreate.length + preview.toUpdate.length} Records
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgChartImport;